import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'motion/react';
import { variants, EASE_OUT } from '../lib/motion-presets';
import AnimatedCounter from './animations/AnimatedCounter';
import Magnetic from './animations/Magnetic';
import './MainHero.css';

const MainHero = ({ onOpenAuthModal }) => {
  const reduce = useReducedMotion();

  // Entrance container — staggers children on mount.
  const container = {
    hidden: {},
    visible: {
      transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 },
    },
  };
  const item = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
    : variants.fadeUp;

  // Each "proof" metric gets a count-up where it's a pure number.
  const proofs = [
    { node: <AnimatedCounter value={100} suffix="%" />, label: 'GST & BIS Verified' },
    { node: <>8-Stage</>, label: 'Custody Order Tracking' },
    { node: <AnimatedCounter value={0} prefix="₹" />, label: 'Buyer Sourcing Fee' },
    { node: <>Insured</>, label: 'Vault-to-Vault Transit' },
  ];

  return (
    <section className="main-hero-section">
      {/* Background Decorative Mesh & Radial Elements */}
      <div className="hero-radial-glow gold-top-glow"></div>
      <div className="hero-radial-glow sapphire-right-glow"></div>
      <div className="hero-mesh-overlay"></div>
      <div className="noise-overlay" aria-hidden="true"></div>

      <div className="jmi-container hero-container">
        <motion.div
          className="hero-content-block"
          initial="hidden"
          animate="visible"
          variants={container}
        >
          {/* Tagline Badge */}
          <motion.div className="hero-network-badge" variants={item}>
            <span className="badge-sparkle">✦</span>
            <span>INDIA'S GOVERNED B2B JEWELLERY NETWORK</span>
          </motion.div>

          {/* Headline */}
          <motion.h1 className="hero-main-title" variants={item}>
            <span className="title-words">Buy. Sell. Manage. Grow.</span>
            <span className="gold-gradient-text gold-gradient-text-animated title-subline">
              One B2B Platform for the Jewellery Trade.
            </span>
          </motion.h1>

          {/* Subtext */}
          <motion.p className="hero-lead-text" variants={item}>
            Discover verified jewellery suppliers, manage wholesale orders,
            coordinate fulfilment and build long-term business relationships
            through JMI.
          </motion.p>

          {/* Conversion Decision Engine: Buyer vs Seller */}
          <motion.div className="hero-conversion-cluster" variants={item}>
            <div className="decision-prompt-label">
              <span>SELECT YOUR BUSINESS PORTAL TO BEGIN</span>
            </div>

            <div className="primary-actions-row">
              <Magnetic strength={14} radius={140}>
                <Link to="/buyer" className="hero-btn hero-btn-buyer">
                  <div className="btn-glow-pulse"></div>
                  <div className="btn-inner">
                    <span className="portal-icon-lead">🛍️</span>
                    <div className="btn-label-group">
                      <span className="btn-main-title">I'm a Buyer</span>
                      <span className="btn-sub-caption">
                        Source Wholesale Stock
                      </span>
                    </div>
                    <span className="btn-arrow-icon">→</span>
                  </div>
                </Link>
              </Magnetic>

              <Magnetic strength={14} radius={140}>
                <Link to="/seller" className="hero-btn hero-btn-seller">
                  <div className="btn-inner">
                    <span className="portal-icon-lead">💎</span>
                    <div className="btn-label-group">
                      <span className="btn-main-title">I'm a Seller</span>
                      <span className="btn-sub-caption">
                        Expand B2B Sales Channel
                      </span>
                    </div>
                    <span className="btn-arrow-icon">→</span>
                  </div>
                </Link>
              </Magnetic>
            </div>

            {/* Secondary Navigation Actions */}
            <div className="secondary-actions-row">
              <a href="#how-it-works-steps" className="sec-link">
                <span>Explore How JMI Works</span>
                <span className="sec-arrow">↓</span>
              </a>
              <span className="sec-divider">|</span>
              <a href="#apps" className="sec-link">
                <span>Download Mobile Apps</span>
              </a>
              <span className="sec-divider">|</span>
              <button
                type="button"
                className="sec-link-btn"
                onClick={() =>
                  onOpenAuthModal ? onOpenAuthModal('login') : null
                }
              >
                Sign In to Portal
              </button>
            </div>
          </motion.div>

          {/* Key Platform Verification Metrics */}
          <motion.div
            className="hero-trust-proof-strip"
            variants={item}
            transition={{ duration: 0.6, ease: EASE_OUT }}
          >
            {proofs.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="proof-separator" />}
                <div className="proof-item">
                  <span className="proof-val">{p.node}</span>
                  <span className="proof-label">{p.label}</span>
                </div>
              </React.Fragment>
            ))}
          </motion.div>

          {/* Scroll cue */}
          <motion.div
            className="scroll-pulse"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: reduce ? 0.5 : [0.3, 0.7, 0.3] }}
            transition={{ duration: 2.4, repeat: reduce ? 0 : Infinity, ease: 'easeInOut', delay: 1.2 }}
            style={{ marginTop: '32px' }}
          >
            <span className="scroll-pulse-dot" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default MainHero;
